/**
 * WaveBriefingPage — `/waves/:id/briefing`
 *
 * The dedicated cinematic surface where a brand watches WAVE assemble
 * their cohort in 4 acts. This page intentionally lives on its own URL
 * (not as a step inside BuildWavePage) because:
 *   - The moment deserves its own canvas (decision a from spec)
 *   - The brand should be able to back-button to it from the Wave
 *     Control Room via the future "Manifest" tab
 *   - The animation can run unhindered by sidebar/header chrome
 *
 * Renders BriefingCanvas which owns the animation state machine. This
 * page is intentionally thin — just fetch + auth shell.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useBrandProfile } from '../contexts/BrandProfileContext';
import BriefingCanvas from '../components/briefing/BriefingCanvas';
import BriefingLoadingState from '../components/briefing/BriefingLoadingState';

const API = process.env.REACT_APP_BACKEND_URL;
// Waves in any of these statuses already fired — no re-ignition allowed.
// We redirect the brand straight to the Control Room. This is the
// defensive route guard for open product question Q2 from the iter-88
// handoff.
const LOCKED_WAVE_STATUSES = new Set(['live', 'sent', 'active', 'completed', 'paused']);

export default function WaveBriefingPage() {
  const { id: waveId } = useParams();
  const navigate = useNavigate();
  const { profile } = useBrandProfile() || {};
  const [briefing, setBriefing] = useState(null);
  const [waveName, setWaveName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Briefing payload (Acts 1-4 data)
        const { data } = await axios.get(
          `${API}/api/brand/waves/${waveId}/briefing`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setBriefing(data);

        // Fetch wave list — we need (a) the wave_name for the header
        // chrome and (b) the wave_status to enforce the no-re-ignition
        // route guard. Both come from the same call.
        try {
          const { data: waveList } = await axios.get(
            `${API}/api/brand/waves`,
            { withCredentials: true },
          );
          const wave = (Array.isArray(waveList) ? waveList : []).find(
            w => w.id === waveId,
          );
          if (wave && !cancelled) {
            setWaveName(wave.campaign_name || wave.goal || '');
            const status = String(wave.status || '').toLowerCase();
            if (LOCKED_WAVE_STATUSES.has(status)) {
              navigate(`/waves/${waveId}/control`, { replace: true });
              return;
            }
            // Stash the status on the briefing object so the canvas
            // can read it as a defensive second-line guard.
            setBriefing(prev => prev ? { ...prev, wave_status: status } : prev);
          }
        } catch {
          // Header just shows "Brand · Briefing" without the wave name
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Briefing unavailable.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [waveId, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center text-center px-6">
        <p
          data-testid="briefing-error"
          className="text-sm text-gray-400 mb-2"
        >
          Briefing unavailable
        </p>
        <p className="text-[11px] text-gray-600 max-w-md">{String(error)}</p>
      </div>
    );
  }

  if (!briefing) {
    return <BriefingLoadingState />;
  }

  return (
    <BriefingCanvas
      briefing={briefing}
      brandName={profile?.brand_name}
      waveName={waveName}
    />
  );
}
